import {
    AdditionalInfoType,
    AdditionalInfoFormViewNext,
    // Narrative endpoint types
    CreateNarrative,
    CreateNarrativeResponse,
    DeleteNarrative,
    DeleteNarrativeResponse,
    EditNarrative,
    EditScheduledEventNarrative,
    SetNarrativePriority,
    SetScheduledEventNarrativePriority,
    PinScheduledEventNarrative,
    ListNarratives,
    ListNarrativesResponse,
    ListNarrativesByIds,
    ListScheduledEventNarratives,
    ListPinnedNarratives,
    ListPinnedNarrativesResponse,
    PinNarrative,
    PinNarrativeResponse,
    ApiResultNarrativeEditResponse,
    ApiResultNarrativePriorityResponse,
    ApiResultNarrativeScheduledEventEditResponse,
    ApiResultNarrativeScheduledEventPriorityResponse,
    ApiResultNarrativeScheduledEventPinResponse,
    NarrativePriorityEnumType,
    // TODO: RND-61328 remove when rollOutCadNarrativeFilteringAndFocusImprovementsEnabled is enabled globally
    CreateAdditionalInfoNext,
    CreateAdditionalInfoNextResponse,
    DeleteAdditionalInfoLegacy,
    DeleteAdditionalInfoLegacyResponse,
    GetAdditionalInfosByCursor,
    GetAdditionalInfosByCursorResponse,
    GetAdditionalInfosByIds,
    GetScheduledEventsAdditionalInfos,
} from '../../types';
import { useApiClient } from '~/api-client';
import { ListDirection } from './types';
import { AxiosResponse } from 'axios-cad-nx';
import { useCallback, useMemo } from 'react';

/**
 * Legacy-endpoint fallback page size. Only used when calling the deprecated
 * GET `/v2/next/events/{id}/additional_infos` endpoint, which requires a
 * concrete page size. The new `narrative/list` endpoint omits it and lets the
 * BE resolve the default from the NARRATIVE_DEFAULT_PAGE_SIZE preference.
 * TODO: RND-61328 remove together with the legacy GET branch when the
 *       narrative-filtering rollout is fully shipped.
 */
const DEFAULT_PAGE_SIZE = 100;

interface GetInfosByCursorParams {
    agencyEventId: number;
    limit?: number;
    direction?: ListDirection;
    filter?: Array<AdditionalInfoType>;
    cursor?: string;
    aroundId?: number;
    includeEventNarratives?: boolean;
    agencyTypeGlobalAttrIds?: number[];
}

export interface AdditionalInfosResource {
    deleteInfo: (
        agencyEventId: number,
        narrativeId: number
    ) => Promise<AxiosResponse<DeleteNarrativeResponse | DeleteAdditionalInfoLegacyResponse>>;
    editInfo: (params: {
        additionalInfoId: number;
        text?: string | null;
    }) => Promise<AxiosResponse<ApiResultNarrativeEditResponse>>;
    setNarrativePriority: (params: {
        additionalInfoId: number;
        priority: string;
    }) => Promise<AxiosResponse<ApiResultNarrativePriorityResponse>>;
    editScheduledEventNarrative: (params: {
        scheduledEventId: number;
        text: string;
    }) => Promise<AxiosResponse<ApiResultNarrativeScheduledEventEditResponse>>;
    setScheduledNarrativePriority: (params: {
        scheduledEventId: number;
        priority: NarrativePriorityEnumType;
    }) => Promise<AxiosResponse<ApiResultNarrativeScheduledEventPriorityResponse>>;
    pinScheduledEventNarrative: (params: {
        scheduledEventId: number;
        action: 'PIN' | 'UNPIN';
    }) => Promise<AxiosResponse<ApiResultNarrativeScheduledEventPinResponse>>;
    getInfosByCursor: (
        params: GetInfosByCursorParams
    ) => Promise<AxiosResponse<ListNarrativesResponse | GetAdditionalInfosByCursorResponse>>;
    getScheduledEventInfos: (params: {
        agencyEventId: number;
    }) => Promise<AxiosResponse<ListNarrativesResponse>>;
    createAdditionalInfo: (
        info: AdditionalInfoFormViewNext
    ) => Promise<AxiosResponse<CreateNarrativeResponse | CreateAdditionalInfoNextResponse>>;
    getInfosByIds: (
        agencyEventId: number,
        data: { ids: number[] }
    ) => Promise<AxiosResponse<ListNarrativesResponse | GetAdditionalInfosByCursorResponse>>;
    getPinnedNarratives: (params: {
        agencyEventId: number;
    }) => Promise<AxiosResponse<ListPinnedNarrativesResponse>>;
    pinNarrative: (params: {
        agencyEventId: number;
        additionalInfoId: number;
    }) => Promise<AxiosResponse<PinNarrativeResponse>>;
    unpinNarrative: (params: {
        agencyEventId: number;
        additionalInfoId: number;
    }) => Promise<AxiosResponse<PinNarrativeResponse>>;
}

export function useAdditionalInfosResource(
    useNarrativeEndpoints: boolean
): AdditionalInfosResource {
    const apiClient = useApiClient();

    const deleteInfo = useCallback<AdditionalInfosResource['deleteInfo']>(
        async (agencyEventId, narrativeId) => {
            if (useNarrativeEndpoints) {
                return await apiClient.request<DeleteNarrative>({
                    method: 'POST',
                    url: `narrative/delete`,
                    data: { agencyEventId, narrativeId },
                    shouldFailSilently: true,
                });
            }
            // TODO: RND-61328 remove when rollOutCadNarrativeFilteringAndFocusImprovementsEnabled is enabled globally
            return await apiClient.request<DeleteAdditionalInfoLegacy>({
                method: 'DELETE',
                url: `v2/events/${agencyEventId}/additional_infos/${narrativeId}/`,
                shouldFailSilently: true,
            });
        },
        [apiClient, useNarrativeEndpoints]
    );

    const getInfosByCursor = useCallback<AdditionalInfosResource['getInfosByCursor']>(
        async ({
            agencyEventId,
            limit: pageSize,
            direction = ListDirection.HISTORIC,
            filter = [AdditionalInfoType.USER, AdditionalInfoType.SYSTEM],
            cursor,
            aroundId,
            includeEventNarratives = true,
            agencyTypeGlobalAttrIds,
        }) => {
            if (useNarrativeEndpoints) {
                // Direction + cursor → directional wire field. The new endpoint
                // doesn't take `direction` or `nextCursor`; presence of
                // olderCursor vs newerCursor (vs aroundId vs neither) tells the
                // BE which page shape to return.
                const olderCursor = direction === ListDirection.HISTORIC ? cursor : undefined;
                const newerCursor = direction === ListDirection.CATCHUP ? cursor : undefined;
                return await apiClient.request<ListNarratives>({
                    method: 'POST',
                    url: `narrative/list`,
                    data: {
                        agencyEventId,
                        additionalInfoTypes: filter,
                        agencyTypeGlobalAttrIds,
                        includeEventNarratives,
                        olderCursor,
                        newerCursor,
                        aroundId,
                        pageSize,
                    },
                    shouldFailSilently: true,
                });
            }
            // TODO: RND-61328 remove when rollOutCadNarrativeFilteringAndFocusImprovementsEnabled is enabled globally
            return await apiClient.request<GetAdditionalInfosByCursor>({
                method: 'GET',
                url: `v2/next/events/${agencyEventId}/additional_infos`,
                params: {
                    // Legacy V2 endpoint's cursor is a numeric id on the wire; the
                    // store carries the shared opaque string token, so convert only
                    // at this deprecated boundary (RND-61328).
                    nextCursor: cursor != null ? Number(cursor) : undefined,
                    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
                    direction,
                    filter,
                    includeEventNarratives,
                },
                shouldFailSilently: true,
            });
        },
        [apiClient, useNarrativeEndpoints]
    );

    const getScheduledEventInfos = useCallback<AdditionalInfosResource['getScheduledEventInfos']>(
        async ({ agencyEventId }) => {
            if (useNarrativeEndpoints) {
                return await apiClient.request<ListScheduledEventNarratives>({
                    method: 'POST',
                    url: `narrative/scheduled_event/list`,
                    data: { scheduledEventId: agencyEventId },
                    shouldFailSilently: true,
                });
            }
            // TODO: RND-61328 remove when rollOutCadNarrativeFilteringAndFocusImprovementsEnabled is enabled globally
            return ((await apiClient.request<GetScheduledEventsAdditionalInfos>({
                method: 'GET',
                url: `v2/next/scheduled_events/${agencyEventId}/additional_infos`,
                shouldFailSilently: true,
            })) as unknown) as AxiosResponse<ListNarrativesResponse>;
        },
        [apiClient, useNarrativeEndpoints]
    );

    const createAdditionalInfo = useCallback<AdditionalInfosResource['createAdditionalInfo']>(
        async ({
            agencyEventId,
            text,
            isHighPriority,
            crossClassOptions,
            toExternalCadIntegrations,
        }) => {
            if (useNarrativeEndpoints) {
                return await apiClient.request<CreateNarrative>({
                    method: 'POST',
                    url: `narrative/create`,
                    data: {
                        agencyEventId,
                        text,
                        priority: isHighPriority ? 'HIGH_PRIORITY_UPDATE' : 'STANDARD_UPDATE',
                        crossClassAgencyTypes: crossClassOptions,
                        toExternalCadIntegrations,
                    },
                    shouldFailSilently: true,
                });
            }
            // TODO: RND-61328 remove when rollOutCadNarrativeFilteringAndFocusImprovementsEnabled is enabled globally
            return await apiClient.request<CreateAdditionalInfoNext>({
                method: 'POST',
                url: `v2/additional_infos`,
                data: {
                    agencyEventId,
                    text,
                    priority: isHighPriority ? 'HIGH_PRIORITY_UPDATE' : 'STANDARD_UPDATE',
                    crossClassAgencyTypes: crossClassOptions,
                    toExternalCadIntegrations,
                },
                shouldFailSilently: true,
            });
        },
        [apiClient, useNarrativeEndpoints]
    );

    const editInfo = useCallback<AdditionalInfosResource['editInfo']>(
        async ({ additionalInfoId, text }) => {
            return await apiClient.request<EditNarrative>({
                method: 'POST',
                url: `narrative/edit`,
                data: {
                    additionalInfoId,
                    text: text ?? undefined,
                } as EditNarrative['data'],
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    const setNarrativePriority = useCallback<AdditionalInfosResource['setNarrativePriority']>(
        async ({ additionalInfoId, priority }) => {
            return await apiClient.request<SetNarrativePriority>({
                method: 'POST',
                url: `narrative/priority`,
                data: { additionalInfoId, priority } as SetNarrativePriority['data'],
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    const editScheduledEventNarrative = useCallback<
        AdditionalInfosResource['editScheduledEventNarrative']
    >(
        async ({ scheduledEventId, text }) => {
            return await apiClient.request<EditScheduledEventNarrative>({
                method: 'POST',
                url: `narrative/scheduled_event/edit`,
                data: { scheduledEventId, text },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    const setScheduledNarrativePriority = useCallback<
        AdditionalInfosResource['setScheduledNarrativePriority']
    >(
        async ({ scheduledEventId, priority }) => {
            return await apiClient.request<SetScheduledEventNarrativePriority>({
                method: 'POST',
                url: `narrative/scheduled_event/priority`,
                data: {
                    scheduledEventId,
                    priority,
                },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    const pinScheduledEventNarrative = useCallback<
        AdditionalInfosResource['pinScheduledEventNarrative']
    >(
        async ({ scheduledEventId, action }) => {
            return await apiClient.request<PinScheduledEventNarrative>({
                method: 'POST',
                url: `narrative/scheduled_event/pin`,
                data: { scheduledEventId, action },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    const getInfosByIds = useCallback<AdditionalInfosResource['getInfosByIds']>(
        async (agencyEventId, data) => {
            if (useNarrativeEndpoints) {
                return await apiClient.request<ListNarrativesByIds>({
                    method: 'POST',
                    url: `narrative/list_by_ids`,
                    data: { agencyEventId, narrativeIds: data.ids },
                    shouldFailSilently: true,
                });
            }
            // TODO: RND-61328 remove when rollOutCadNarrativeFilteringAndFocusImprovementsEnabled is enabled globally
            return await apiClient.request<GetAdditionalInfosByIds>({
                method: 'POST',
                url: `v2/next/events/${agencyEventId}/additional_infos/ids`,
                data: data.ids,
                shouldFailSilently: true,
            });
        },
        [apiClient, useNarrativeEndpoints]
    );

    const getPinnedNarratives = useCallback<AdditionalInfosResource['getPinnedNarratives']>(
        async ({ agencyEventId }) => {
            return await apiClient.request<ListPinnedNarratives>({
                method: 'POST',
                url: `narrative/pinned`,
                data: { agencyEventId },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    const pinNarrative = useCallback<AdditionalInfosResource['pinNarrative']>(
        async ({ agencyEventId, additionalInfoId }) => {
            return await apiClient.request<PinNarrative>({
                method: 'POST',
                url: `narrative/pin`,
                data: { action: 'PIN', additionalInfoId, agencyEventId },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    const unpinNarrative = useCallback<AdditionalInfosResource['unpinNarrative']>(
        async ({ agencyEventId, additionalInfoId }) => {
            return await apiClient.request<PinNarrative>({
                method: 'POST',
                url: `narrative/pin`,
                data: { action: 'UNPIN', additionalInfoId, agencyEventId },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    // Stable identity matters: downstream useMemo (controller in use-additional-infos)
    // depends on this object, and a new ref every render makes its effects re-run on every render.
    return useMemo(
        () => ({
            createAdditionalInfo,
            deleteInfo,
            editInfo,
            setNarrativePriority,
            editScheduledEventNarrative,
            setScheduledNarrativePriority,
            pinScheduledEventNarrative,
            getInfosByCursor,
            getInfosByIds,
            getScheduledEventInfos,
            getPinnedNarratives,
            pinNarrative,
            unpinNarrative,
        }),
        [
            createAdditionalInfo,
            deleteInfo,
            editInfo,
            setNarrativePriority,
            editScheduledEventNarrative,
            setScheduledNarrativePriority,
            pinScheduledEventNarrative,
            getInfosByCursor,
            getInfosByIds,
            getScheduledEventInfos,
            getPinnedNarratives,
            pinNarrative,
            unpinNarrative,
        ]
    );
}
